//@ts-check
import {DatePicker, Link, TextInput, Toggle} from '@/components-v2'
import {SegmentDropdown} from '@/features/ProductReview/components/ui/Segment/SegmentDropdown'
import {formatDateToMMDDYYYY} from '@/helpers/shadcn/utils'
import {useFeatureFlagLanguages} from '@/hooks/useFeatureFlagLanguages'
import {selectCountryCode} from '@/store/slices/countryCodeSlice'
import {
  DEFAULT_LOCALE_LABEL,
  LOCALE_COLUMN_LABELS,
  getLocaleHeader,
  getProductListColumnsForCountryCode,
} from '@/utils/constants/productListColumns'
import {ROUTES} from '@/utils/constants/routesConstants'
import {useMemo, useState} from 'react'
import {useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {useProductListEditContext} from '../features/ProductList/ProductListTabContent/ProductListEditContext'
import {POSITIVE_INTEGER_COLUMN_KEYS, isPositiveInteger} from '../features/ProductList/ProductListTabContent/constants'

const toSafeRouteParam = value => {
  const raw = String(value ?? '').trim()

  if (!raw || raw.includes('..') || !/^[A-Za-z0-9._-]+$/.test(raw)) {
    return ''
  }

  return encodeURIComponent(raw)
}

/** Derives a display locale (e.g. `ES-PE`) from the non-English language returned by the languages feature flag. */
const getLocaleLabelFromLanguages = languages => {
  const localizedLanguage = languages?.find(language => language.code !== 'en') ?? languages?.[0]
  return localizedLanguage?.code ? localizedLanguage.code.replace('_', '-').toUpperCase() : DEFAULT_LOCALE_LABEL
}

const ProductLinkCell = ({text, onClick}) =>
  onClick ? <Link useAnchor={false} label={text ?? '-'} onClick={onClick} /> : (text ?? '-')

export const getProductListColumnsForCountry = (countryCode, localeLabel) =>
  getProductListColumnsForCountryCode(countryCode)
    .filter(column => !column.visibleForCountries || column.visibleForCountries.includes(countryCode))
    .map(column =>
      LOCALE_COLUMN_LABELS[column.columnKey]
        ? {...column, header: getLocaleHeader(column.columnKey, localeLabel)}
        : column
    )

export const getMandatoryEditableColumns = countryCode =>
  getProductListColumnsForCountry(countryCode).filter(col => col.editable && col.mandatory)

const DATE_COLUMN_KEYS = new Set(['newSkuEndDate', 'newSkuStartDate'])

const KEYWORDS_COLUMN_KEYS = new Set(['keywords_en'])
const MAX_VISIBLE_KEYWORDS = 3

const formatKeywords = rawValue => {
  const keywords = String(rawValue ?? '')
    .split(',')
    .map(keyword => keyword.trim())
    .filter(Boolean)

  if (keywords.length === 0) return '-'

  const visibleKeywords = keywords.slice(0, MAX_VISIBLE_KEYWORDS)
  const text = visibleKeywords.join(', ') + (keywords.length > MAX_VISIBLE_KEYWORDS ? ', ...' : '')

  return <span title={keywords.join(', ')}>{text}</span>
}

// Long unbroken values (e.g. a display name with no spaces) can't wrap and would stretch the column,
// so cap the cell width and show the full value on hover instead.
const TruncatedText = ({text}) => (
  <span className="block max-w-[350px] truncate" title={text}>
    {text}
  </span>
)

const formatDisplayValue = (columnKey, rawValue) => {
  if (DATE_COLUMN_KEYS.has(columnKey)) return formatDateToMMDDYYYY(rawValue)
  if (KEYWORDS_COLUMN_KEYS.has(columnKey)) return formatKeywords(rawValue)
  if (rawValue == null || rawValue === '') return '-'
  return <TruncatedText text={String(rawValue)} />
}

const MAX_VISIBLE_SEGMENTS = 3

const formatSegments = segments => {
  if (!Array.isArray(segments) || segments.length === 0) return '-'

  const names = segments.map(s => s.name ?? s.id)
  const visibleNames = names.slice(0, MAX_VISIBLE_SEGMENTS)
  const text = visibleNames.join(', ') + (names.length > MAX_VISIBLE_SEGMENTS ? ', ...' : '')

  return <span title={names.join(', ')}>{text}</span>
}

const SegmentCell = ({rawValue, productId}) => {
  const {isEditMode, editedValues, onEditValueChange, frozenProductIds = new Set()} = useProductListEditContext()

  if (!isEditMode) return formatSegments(rawValue)

  const draftSegments = editedValues[productId]?.segmentsToShow
  const currentSegments =
    draftSegments ??
    (Array.isArray(rawValue) ? rawValue.map(segment => ({segmentId: segment.id, segmentName: segment.name})) : [])

  const stopRowSelectClick = e => e.stopPropagation()

  return (
    <div onClick={stopRowSelectClick} onMouseDown={stopRowSelectClick}>
      <SegmentDropdown
        value={currentSegments}
        onChange={segments => onEditValueChange(productId, 'segmentsToShow', segments)}
        disabled={frozenProductIds.has(productId)}
      />
    </div>
  )
}

const EditableCell = ({column, rawValue, rowData, productId}) => {
  const {isEditMode, editedValues, onEditValueChange, frozenProductIds = new Set()} = useProductListEditContext()

  if (!isEditMode) return formatDisplayValue(column.columnKey, rawValue)

  const isFrozen = frozenProductIds.has(productId)
  const rowEdits = editedValues[productId]
  const hasDraftValue = Object.prototype.hasOwnProperty.call(rowEdits ?? {}, column.columnKey)
  const draftValue = rowEdits?.[column.columnKey]
  const currentValue = draftValue ?? rawValue
  const hasInvalidPositiveInteger =
    POSITIVE_INTEGER_COLUMN_KEYS.includes(column.columnKey) && hasDraftValue && !isPositiveInteger(currentValue)
  const {inputType} = column
  const inputPattern =
    inputType === 'integer' ? /^\d*$/ : inputType === 'decimal' ? /^(?:\d+(?:\.\d*)?|\.\d+)?$/ : undefined
  const isAllowedInput = nextValue => !inputPattern || inputPattern.test(nextValue)
  const handleInputChangeCapture = event => {
    if (!inputPattern || !(event.target instanceof HTMLInputElement) || isAllowedInput(event.target.value)) return
    event.target.value = String(currentValue ?? '')
    event.stopPropagation()
  }

  const stopRowSelectClick = e => e.stopPropagation()

  if (column.fieldType === 'date') {
    const isCleared = hasDraftValue && draftValue === null
    const hasNewSkuTagDraft = Object.prototype.hasOwnProperty.call(rowEdits ?? {}, 'newSkuTag')
    const newSkuTagValue = hasNewSkuTagDraft ? rowEdits.newSkuTag : (rowData?.newSkuTag ?? rowData?.isNewProduct)
    const isNewSkuTagDisabled = newSkuTagValue === false || String(newSkuTagValue).toUpperCase() === 'FALSE'
    const isDateDisabled = isFrozen || isCleared || isNewSkuTagDisabled
    const dateDisplayValue = isCleared || isNewSkuTagDisabled ? '' : formatDateToMMDDYYYY(draftValue ?? rawValue) || ''
    return (
      <div onClick={stopRowSelectClick} onMouseDown={stopRowSelectClick}>
        <DatePicker
          selected={dateDisplayValue}
          disabled={isDateDisabled}
          setSelected={newValue => onEditValueChange(productId, column.columnKey, newValue)}
          menuPosition="bottom-left"
          usePortal
          disabledHelperText=""
        />
      </div>
    )
  }

  if (column.fieldType === 'boolean') {
    return (
      <div onClick={stopRowSelectClick} onMouseDown={stopRowSelectClick}>
        <Toggle
          checked={String(currentValue).toUpperCase() === 'TRUE'}
          size="small"
          disabled={isFrozen}
          onUpdate={checked => onEditValueChange(productId, column.columnKey, checked ? 'TRUE' : 'FALSE')}
        />
      </div>
    )
  }

  return (
    <div onClick={stopRowSelectClick} onMouseDown={stopRowSelectClick} onChangeCapture={handleInputChangeCapture}>
      <TextInput
        inputSize="small"
        inputMode={inputType === 'decimal' ? 'decimal' : inputType === 'integer' ? 'numeric' : undefined}
        value={currentValue ?? ''}
        disabled={isFrozen}
        error={hasInvalidPositiveInteger ? 'Must be a positive integer.' : undefined}
        onUpdate={newValue => {
          if (isAllowedInput(newValue)) onEditValueChange(productId, column.columnKey, newValue)
        }}
      />
    </div>
  )
}

/**
 * Builds v2 DataTable column definitions for the product list table, with a clickable
 * navigation cell for productId/productName, and (when editing) text inputs/toggles for
 * editable columns. Also manages visible column key state with localStorage persistence.
 *
 * @param {string[]} [visibleColumnKeysProp] External visible keys. When omitted, state is self-managed.
 */
export const useProductListColumns = visibleColumnKeysProp => {
  const navigate = useNavigate()
  const countryCode = useSelector(selectCountryCode)
  const languages = useFeatureFlagLanguages()
  const localeLabel = useMemo(() => getLocaleLabelFromLanguages(languages), [languages])

  const availableColumns = useMemo(
    () => getProductListColumnsForCountry(countryCode, localeLabel),
    [countryCode, localeLabel]
  )
  const [visibleKeysByCountry, setVisibleKeysByCountry] = useState(/** @type {Record<string, string[]>} */ ({}))
  const internalVisibleKeys = useMemo(
    () =>
      visibleKeysByCountry[countryCode] ?? availableColumns.filter(col => col.defaultVisible).map(col => col.columnKey),
    [visibleKeysByCountry, countryCode, availableColumns]
  )
  const availableColumnKeys = useMemo(() => availableColumns.map(column => column.columnKey), [availableColumns])
  const requestedVisibleColumnKeys = visibleColumnKeysProp ?? internalVisibleKeys
  // Keep cell renderer identities stable during draft updates so inputs aren't remounted.
  const visibleColumnKeys = useMemo(
    () => requestedVisibleColumnKeys.filter(key => availableColumnKeys.includes(key)),
    [requestedVisibleColumnKeys, availableColumnKeys]
  )

  const onApplyColumns = keys => {
    if (!Array.isArray(keys) || keys.length === 0) return

    setVisibleKeysByCountry(previous => ({...previous, [countryCode]: keys}))
  }

  const columns = useMemo(() => {
    const goToProductReview = productId => {
      const safeProductId = toSafeRouteParam(productId)
      return safeProductId ? () => navigate(ROUTES.PRODUCT_REVIEW.replace(':id', safeProductId)) : undefined
    }

    const editableCellData = column => ctx => {
      const original = ctx.row.original
      const rawValue = original?.[column.columnKey]

      if (column.fieldType === 'segment') {
        return <SegmentCell rawValue={rawValue} productId={original?.productId} />
      }

      if (!column.editable) return formatDisplayValue(column.columnKey, rawValue)

      return <EditableCell column={column} rawValue={rawValue} rowData={original} productId={original?.productId} />
    }

    const cellDataByColumn = {
      productId: ctx => (
        <ProductLinkCell text={ctx.row.original?.productId} onClick={goToProductReview(ctx.row.original?.productId)} />
      ),
      productName: ctx => (
        <ProductLinkCell
          text={ctx.row.original?.productName}
          onClick={goToProductReview(ctx.row.original?.productId)}
        />
      ),
      segmentsToShow: ctx => (
        <SegmentCell rawValue={ctx.row.original?.segmentsToShow} productId={ctx.row.original?.productId} />
      ),
    }

    const columnsToShow = visibleColumnKeys
      ? visibleColumnKeys.flatMap(key => {
          const col = availableColumns.find(candidate => candidate.columnKey === key)
          return col ? [col] : []
        })
      : availableColumns.filter(col => col.defaultVisible)

    return columnsToShow.map(column => ({
      columnKey: column.columnKey,
      header: column.header,
      columnFilterPlaceholder: column.header,
      minSize: column.minSize,
      ...(column.filterVariant ? {filterVariant: column.filterVariant} : {}),
      cellData: cellDataByColumn[column.columnKey] ?? editableCellData(column),
      multiSearch: column.multiSearch ?? true,
    }))
  }, [availableColumns, navigate, visibleColumnKeys])

  return {columns, visibleColumnKeys, availableColumns, onApplyColumns}
}
